import React, { useState, useCallback } from "react";
import { useMediaQuery } from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";
import PropTypes from "prop-types";

import { systemsService } from "../../../../services/systems-service";
import { risksService } from "../../../../services/risks-service";
import { criticalRuleService } from "../../../../services/critical-rule-service";

import {
    getUpdatedFormForDeleteEntity,
    getUpdatedFormForEntity,
    getUpdatedFormForMassDeleteEntity,
    getVisibleEntities,
} from "../../../../utils/formViews";

import { CHANGE_STATUS } from "../../../common/const";
import { EntityFormPageViews, EntityFormTabs } from "../../../common/entity-form";
import breakpoints from "../../../common/styles/breakpoints.js";

import RisksView from "../risks-view";
import CriticalRolesView from "../critical-roles-view";
import CriticalProfilesView from "../critical-profiles-view";

const tabs = [
    {
        id: "risks",
        titleId: "matrix-headers-form-content.tabs.risk",
    },
    {
        id: "critical-roles",
        titleId: "matrix-headers-form-content.tabs.critical-roles",
    },
    {
        id: "critical-profiles",
        titleId: "matrix-headers-form-content.tabs.critical-profiles",
    },
];

const checkItemsForDuplicates = (items) => {
    const keysMap = {};
    const entityKeyFields = ["riskId", "roleName", "profile", "system"];

    const getItemKey = (i) => entityKeyFields.map((field) => i[field]).join("-");

    items.forEach((item) => {
        if (item.changeStatus === CHANGE_STATUS.DELETE) return;

        const key = getItemKey(item);

        if (!(key in keysMap)) {
            keysMap[key] = 0;
        }

        keysMap[key] += 1;
    });

    items.forEach((item) => {
        if (item.changeStatus === CHANGE_STATUS.DELETE) return;

        const key = getItemKey(item);

        if (keysMap[key] === 1) {
            entityKeyFields.forEach((field) => {
                const fieldHasError = item[`error_${field}`] && item[`error_${field}`]["code"] === "duplicate";

                if (fieldHasError) {
                    delete item[`error_${field}`];
                }
            });
        }
    });
};

const removeDuplicateError = (item) => {
    ["riskId"].forEach((field) => {
        if (item[`error_${field}`] && item[`error_${field}`].code === "duplicate") {
            item[`error_${field}`] = null;
        }
    });
};

const getUpdatedFormForRisk = (form, risk, field, value) => {
    return getUpdatedFormForEntity(form, risk, field, value, "risks", ["riskId"]);
};

const getUpdatedFormForCritRole = (form, criticalRole, field, value) => {
    return getUpdatedFormForEntity(form, criticalRole, field, value, "criticalRoles", ["roleName", "system"]);
};

const getUpdatedFormForCritProfile = (form, criticalProfile, field, value) => {
    return getUpdatedFormForEntity(form, criticalProfile, field, value, "criticalProfiles", ["profile", "system"]);
};

const getValidationErrorMessage = (error) => error.detail || error.title;

const validateRisk = (item, risk, error) => {
    if (error) {
        item["error_riskId"] = {
            value: item["riskId"],
            type: "validation",
            message: getValidationErrorMessage(error),
        };

        item["description"] = "";

        return;
    }

    item["error_riskId"] = null;
    item["description"] = risk?.description ?? "";
};

const validateCriticalRole = (item, criticalRole, errorSystem, errorRole) => {
    if (errorSystem) {
        item["error_system"] = {
            value: item["system"],
            type: "validation",
            message: getValidationErrorMessage(errorSystem),
        };
    } else {
        item["error_system"] = null;
    }

    if (errorRole) {
        item["error_roleName"] = {
            value: item["roleName"],
            type: "validation",
            message: getValidationErrorMessage(errorRole),
        };
    } else {
        item["error_roleName"] = null;
    }

    item["description"] = criticalRole?.description ?? "";
};

const validateCriticalProfile = (item, criticalProfile, errorSystem, errorProfile) => {
    if (errorSystem) {
        item["error_system"] = {
            value: item["system"],
            type: "validation",
            message: getValidationErrorMessage(errorSystem),
        };
    } else {
        item["error_system"] = null;
    }

    if (errorProfile) {
        item["error_profile"] = {
            value: item["profile"],
            type: "validation",
            message: getValidationErrorMessage(errorProfile),
        };
    } else {
        item["error_profile"] = null;
    }

    item["description"] = criticalProfile?.description ?? "";
};

const getSystemForEditRow = async (field, value, item, signal) => {
    let system, systemId, errorSystem;

    if (field === "system") {
        if (value) {
            try {
                system = await systemsService.getSystemDetailed(value, signal);
            } catch (e) {
                errorSystem = e;
            }
        }

        systemId = system?.id ?? value;
    } else {
        systemId = item["system"];
        errorSystem = item["error_system"];
    }

    return { systemId, errorSystem };
};

const MatrixHeadersFormViews = ({ form, setForm, formDisabled, validationErrors, errorScroll, setErrorScroll }) => {
    const matches1024 = useMediaQuery(`(min-width:  ${breakpoints.minLaptopBreakpoint})`);

    const [currentView, setCurrentView] = useState(tabs[0].id);

    const addRisk = () => {
        const risk = {
            id: nanoid(),
            riskId: "",
            description: "",
            active: true,
            changeStatus: CHANGE_STATUS.ADD,
        };

        setForm((form) => ({
            ...form,
            risks: [risk, ...form.risks],
        }));
    };

    const deleteRisk = (id) => {
        setForm((form) => {
            const { newForm } = getUpdatedFormForDeleteEntity(form, id, "risks");
            newForm.risks.forEach((item) => {
                removeDuplicateError(item);
            });
            return newForm;
        });
    };

    const deleteRiskMass = useCallback(
        (ids) => {
            setForm((form) => {
                const newForm = getUpdatedFormForMassDeleteEntity(form, ids, "risks");
                newForm.risks.forEach((item) => {
                    removeDuplicateError(item);
                });
                return newForm;
            });
        },
        [setForm]
    );

    const editRisk = async (row, field, value) => {
        if (field === "active") {
            setForm((form) => {
                const { newForm } = getUpdatedFormForRisk(form, row, field, value);
                return newForm;
            });
            return;
        }

        let risk, error;

        setForm((form) => {
            const { newForm, item } = getUpdatedFormForRisk(form, row, field, value);
            if (item) {
                checkItemsForDuplicates(newForm.risks);
                validateRisk(item, risk, error);
            }
            return newForm;
        });
    };

    const checkRisk = async (row, field, value, signal) => {
        if (field === "active") {
            return;
        }

        let risk, error;

        if (value) {
            try {
                risk = await risksService.getRiskDetailed(value, signal);
            } catch (e) {
                error = e;
            }
        }

        const newRiskValue = risk ? risk.id : value;

        setForm((form) => {
            const { newForm, item } = getUpdatedFormForRisk(form, row, field, newRiskValue);
            if (item) {
                checkItemsForDuplicates(newForm.risks);
                validateRisk(item, risk, error);
            }
            return newForm;
        });
    };

    const addCriticalRole = () => {
        const critRole = {
            id: nanoid(),
            roleName: "",
            system: "",
            description: "",
            active: true,
            changeStatus: CHANGE_STATUS.ADD,
        };

        setForm((form) => ({
            ...form,
            criticalRoles: [critRole, ...form.criticalRoles],
        }));
    };

    const deleteCriticalRole = (id) => {
        setForm((form) => {
            const { newForm } = getUpdatedFormForDeleteEntity(form, id, "criticalRoles");
            checkItemsForDuplicates(newForm.criticalRoles);
            return newForm;
        });
    };

    const deleteCriticalRoleMass = useCallback(
        (ids) => {
            setForm((form) => {
                const newForm = getUpdatedFormForMassDeleteEntity(form, ids, "criticalRoles");
                checkItemsForDuplicates(newForm.criticalRoles);
                return newForm;
            });
        },
        [setForm]
    );

    const editCriticalRole = async (row, field, value) => {
        if (field === "active") {
            setForm((form) => {
                const { newForm } = getUpdatedFormForCritRole(form, row, field, value);
                return newForm;
            });
            return;
        }

        let critRole, errorRole;
        let newValue;

        switch (field) {
            case "system":
                newValue = value;
                break;
            case "roleName":
                newValue = value;
                break;
        }

        setForm((form) => {
            const { newForm, item } = getUpdatedFormForCritRole(form, row, field, newValue);
            if (item) {
                checkItemsForDuplicates(newForm.criticalRoles);
                validateCriticalRole(item, critRole, null, errorRole);
            }
            return newForm;
        });
    };
    const checkCriticalRole = async (row, field, value, signal) => {
        if (field === "active") {
            return;
        }

        let critRole, errorRole;
        let newValue;

        const item = form.criticalRoles.find((item) => item.id === row.id);
        const { systemId, errorSystem } = await getSystemForEditRow(field, value, item, signal);
        const critRoleName = field === "roleName" ? value : item["roleName"];

        if (critRoleName) {
            try {
                critRole = await criticalRuleService.getCriticalRoleDetailed(critRoleName, systemId, signal);
            } catch (e) {
                errorRole = e;
            }
        }

        switch (field) {
            case "system":
                newValue = systemId;
                break;
            case "roleName":
                newValue = critRole?.name ?? value;
                break;
        }

        setForm((form) => {
            const { newForm, item } = getUpdatedFormForCritRole(form, row, field, newValue);
            if (item) {
                checkItemsForDuplicates(newForm.criticalRoles);
                validateCriticalRole(item, critRole, errorSystem, errorRole);
            }
            return newForm;
        });
    };

    const addCriticalProfile = () => {
        const critProfile = {
            id: nanoid(),
            profile: "",
            system: "",
            description: "",
            active: true,
            changeStatus: CHANGE_STATUS.ADD,
        };

        setForm((form) => ({
            ...form,
            criticalProfiles: [critProfile, ...form.criticalProfiles],
        }));
    };

    const deleteCriticalProfile = (id) => {
        setForm((form) => {
            const { newForm } = getUpdatedFormForDeleteEntity(form, id, "criticalProfiles");
            checkItemsForDuplicates(newForm.criticalProfiles);
            return newForm;
        });
    };

    const deleteCriticalProfileMass = useCallback(
        (ids) => {
            setForm((form) => {
                const newForm = getUpdatedFormForMassDeleteEntity(form, ids, "criticalProfiles");
                checkItemsForDuplicates(newForm.criticalProfiles);
                return newForm;
            });
        },
        [setForm]
    );

    const editCriticalProfile = async (row, field, value) => {
        if (field === "active") {
            setForm((form) => {
                const { newForm } = getUpdatedFormForCritProfile(form, row, field, value);
                return newForm;
            });
            return;
        }

        let critProfile, errorProfile;
        let newValue;

        switch (field) {
            case "system":
                newValue = value;
                break;
            case "profile":
                newValue = critProfile?.name ?? value;
                break;
        }

        setForm((form) => {
            const { newForm, item } = getUpdatedFormForCritProfile(form, row, field, newValue);
            if (item) {
                checkItemsForDuplicates(newForm.criticalProfiles);
                validateCriticalProfile(item, critProfile, null, errorProfile);
            }
            return newForm;
        });
    };
    const checkCriticalProfile = async (row, field, value, signal) => {
        if (field === "active") {
            return;
        }

        let critProfile, errorProfile;
        let newValue;

        const item = form.criticalProfiles.find((item) => item.id === row.id);
        const { systemId, errorSystem } = await getSystemForEditRow(field, value, item, signal);
        const critProfileName = field === "profile" ? value : item["profile"];

        if (critProfileName) {
            try {
                critProfile = await criticalRuleService.getCriticalProfileDetailed(critProfileName, systemId, signal);
            } catch (e) {
                errorProfile = e;
            }
        }

        switch (field) {
            case "system":
                newValue = systemId;
                break;
            case "profile":
                newValue = critProfile?.name ?? value;
                break;
        }

        setForm((form) => {
            const { newForm, item } = getUpdatedFormForCritProfile(form, row, field, newValue);
            if (item) {
                checkItemsForDuplicates(newForm.criticalProfiles);
                validateCriticalProfile(item, critProfile, errorSystem, errorProfile);
            }
            return newForm;
        });
    };

    const visibleRisks = getVisibleEntities(form.risks);
    const visibleCriticalRoles = getVisibleEntities(form.criticalRoles);
    const visibleCriticalProfiles = getVisibleEntities(form.criticalProfiles);

    const risksErrorIndex = errorScroll?.errorTab === "risks" ? errorScroll.errorIndex : null;

    const criticalRolesErrorIndex = errorScroll?.errorTab === "critical-roles" ? errorScroll.errorIndex : null;

    const criticalProfilesErrorIndex = errorScroll?.errorTab === "critical-profiles" ? errorScroll.errorIndex : null;

    return (
        <EntityFormPageViews errorScroll={errorScroll} setCurrentView={setCurrentView}>
            <EntityFormTabs tabs={tabs} tabId={currentView} setTabId={(id) => setCurrentView(id)} oneVisibleTab={!matches1024} />

            {currentView === "risks" && (
                <RisksView
                    risks={visibleRisks}
                    addRisk={addRisk}
                    deleteRisk={deleteRisk}
                    deleteRiskMass={deleteRiskMass}
                    editRisk={editRisk}
                    disabled={formDisabled}
                    validationErrors={validationErrors}
                    errorIndex={risksErrorIndex}
                    setErrorScroll={setErrorScroll}
                    checkRisk={checkRisk}
                />
            )}

            {currentView === "critical-roles" && (
                <CriticalRolesView
                    criticalRoles={visibleCriticalRoles}
                    addCriticalRole={addCriticalRole}
                    deleteCriticalRole={deleteCriticalRole}
                    deleteCriticalRoleMass={deleteCriticalRoleMass}
                    editCriticalRole={editCriticalRole}
                    disabled={formDisabled}
                    validationErrors={validationErrors}
                    errorIndex={criticalRolesErrorIndex}
                    setErrorScroll={setErrorScroll}
                    checkCriticalRole={checkCriticalRole}
                />
            )}

            {currentView === "critical-profiles" && (
                <CriticalProfilesView
                    criticalProfiles={visibleCriticalProfiles}
                    addCriticalProfile={addCriticalProfile}
                    deleteCriticalProfile={deleteCriticalProfile}
                    deleteCriticalProfileMass={deleteCriticalProfileMass}
                    editCriticalProfile={editCriticalProfile}
                    disabled={formDisabled}
                    validationErrors={validationErrors}
                    errorIndex={criticalProfilesErrorIndex}
                    setErrorScroll={setErrorScroll}
                    checkCriticalProfile={checkCriticalProfile}
                />
            )}
        </EntityFormPageViews>
    );
};

MatrixHeadersFormViews.propTypes = {
    form: PropTypes.object,
    setForm: PropTypes.func,
    formDisabled: PropTypes.bool,
    validationErrors: PropTypes.object,
    errorScroll: PropTypes.shape({
        errorTab: PropTypes.string,
        errorIndex: PropTypes.number,
    }),
    setErrorScroll: PropTypes.func,
};

export default MatrixHeadersFormViews;
